/**
 * Оновлення застосунку — з питанням, а не мовчки.
 *
 * `registerType: "prompt"` у `vite.config.ts` був заявлений з першого дня,
 * але спитати не було кому: жоден код не викликав `updateSW`. Наслідок
 * виявився неочевидним і серйозним — новий service worker щоразу
 * встановлювався й **чекав вічно**, а пристрій продовжував працювати на
 * тій збірці, з якою його вперше відкрили. Розгортання проходили,
 * а до людини не доїжджали.
 *
 * Чому не `autoUpdate`, який просто підмінив би застосунок: оновлення
 * перезавантажує сторінку, а тут пишуть текст. Забрати абзац посеред
 * речення заради нової версії — не той компроміс. Тому смужка внизу
 * і кнопка.
 */
import { Pressable, Stack, Surface, Text } from "@beresta/ui";
import { Suspense, lazy } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { t } from "./i18n.js";

const InstallHint = lazy(() => import("./InstallHint.js"));

/**
 * `overBar` — під смужкою є нижня панель. Спершу смужка прилипала до низу
 * (`sticky`, `z-index: 10`), а панель лежить вище (`z-index: 20`), тож на
 * телефоні саме кнопку «Оновити» й накривало. Тепер вона стоїть над панеллю
 * на тій самій висоті, що й UndoToast; на замку й на ноутбуці панелі немає,
 * і смужка опускається до низу.
 */
export function UpdatePrompt({ overBar = false }: { readonly overBar?: boolean }) {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Перевіряти не лише при завантаженні: вкладку зі щоденником тримають
    // відкритою добами, і без цього оновлення чекало б до перезапуску.
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });

  // Один рядок унизу, не більше (план перенесення дизайну): оновлення
  // важливіше, тож підказка «Встановити» чекає, поки його немає.
  if (!needRefresh) {
    return overBar && installMaybe() ? (
      <Suspense fallback={null}>
        <InstallHint />
      </Suspense>
    ) : null;
  }

  return (
    <Surface className={overBar ? "update update-over-bar" : "update"}>
      <Stack direction="row" gap={2} align="center" wrap>
        <Text style="footnote">{t("update.ready")}</Text>
        <Pressable variant="primary" onClick={() => void updateServiceWorker(true)}>
          {t("update.apply")}
        </Pressable>
      </Stack>
    </Surface>
  );
}

/**
 * Дешева перевірка до лінивого модуля: уже встановлено або вже відмовились —
 * модуль підказки не вантажиться взагалі.
 */
function installMaybe(): boolean {
  if (globalThis.matchMedia?.("(display-mode: standalone)").matches) return false;
  try {
    return localStorage.getItem("beresta:install-dismissed") !== "1";
  } catch {
    return false;
  }
}
